import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function SignupPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const storedRef = (() => {
    try {
      const v = JSON.parse(localStorage.getItem("zuntria_ref") || "null");
      return v && new Date(v.expires) > new Date() ? v.code : null;
    } catch (e) { return null; }
  })();
  const ref = params.get("ref") || storedRef;

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (!agreed) return toast.error("Please accept the terms to continue");
    setLoading(true);
    try {
      await signup(name, email, password, ref);
      toast.success("Account created!");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  const googleSignup = () => {
    if (!agreed) return toast.error("Please accept the terms to continue");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="zx-hero-bg relative overflow-hidden">
      <div className="zx-tunnel" aria-hidden />
      <div className="zx-streaks" aria-hidden />
      <div className="zx-tunnel-core" aria-hidden />
      <div className="relative mx-auto flex min-h-[80vh] max-w-md flex-col justify-center px-6 py-12">
      <div className="rounded-2xl border border-border bg-card p-8">
        <h1 className="font-display text-2xl font-medium tracking-tight">Create your account</h1>
        <p className="mt-1 text-sm text-muted-foreground">Start using Zuntria in seconds.{ref ? ` You've been invited — enjoy £0.50 welcome cashback!` : ""}</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="signup-form">
          <div className="space-y-2">
            <Label htmlFor="name">Full name</Label>
            <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} className="h-11" data-testid="signup-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="signup-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" data-testid="signup-password-input" />
          </div>
          <label className="flex items-start gap-2.5 text-xs leading-relaxed text-muted-foreground" data-testid="signup-legal-label">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              required
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-border accent-primary"
              data-testid="signup-legal-checkbox"
            />
            <span>
              I understand Zuntria is not a bank. My money is processed and held by licensed payment partners (PayPal, Stripe, Paystack). I agree to the{" "}
              <Link to="/terms" className="text-primary hover:underline">Terms & Conditions</Link> and{" "}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
            </span>
          </label>
          <Button type="submit" className="h-11 w-full" disabled={loading || !agreed} data-testid="signup-submit-btn">
            {loading ? "Creating…" : "Create account"}
          </Button>
        </form>

        <div className="relative my-6 text-center">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
          <span className="relative bg-card px-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">or</span>
        </div>

        <Button variant="outline" className="h-11 w-full" onClick={googleSignup} data-testid="google-signup-btn">
          Continue with Google
        </Button>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already have an account? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
        </p>
      </div>
      </div>
    </div>
  );
}
